import cx from 'classnames';

import type { Card } from '~/interfaces/home_page';
import type { Post } from '~/interfaces/post';
import { BORDER_RADIUS_OPTIONS, SHADOW_OPTIONS } from '~/utils/designOptions';

import Details from './Details';
import { HOVER_SHADOW_OPTIONS } from './hoverShadowOptions';
import Insights from './Insights';
import PostImage from './PostImage';
import PostLink from './PostLink';
import Premium from './Premium';
import Tags from './Tags';
import Titles from './Titles';

interface Props {
  post: Post;
  card: Card;
  orientation?: 'horizontal' | 'vertical';
}

const PostCardv2 = ({ post, card, orientation = 'vertical' }: Props) => {
  const isPremium = post.audience === 'premium';
  const radiusClass = BORDER_RADIUS_OPTIONS[card?.border_radius || 'none'];
  const shadowClass = SHADOW_OPTIONS[card?.shadow || 'none'];
  const hoverShadowClass = HOVER_SHADOW_OPTIONS[card?.hover_shadow || 'none'];
  const fontFamily = card.font;
  const isVertical = orientation === 'vertical';
  const verticalClass = 'flex-col';
  const isHorizontal = orientation === 'horizontal';
  const horizontalClass = 'flex-col md:flex-row';
  const commentCount = post?.comment_count || 0;
  const likeCount = post?.likes?.length || 0;

  return (
    <div
      suppressHydrationWarning
      className={cx('group h-full overflow-hidden transition-all', shadowClass, hoverShadowClass, radiusClass)}
      style={{
        backgroundColor: card.bg_color,
        borderColor: card.border_color,
      }}
    >
      <PostLink
        slug={post?.slug}
        style={{
          backgroundColor: card.bg_color,
          borderColor: card.border_color,
        }}
        className={cx(
          'group flex h-full w-full border transition-all group-hover:brightness-110',
          radiusClass,
          isVertical && verticalClass,
          isHorizontal && horizontalClass,
        )}
      >
        <div className="w-full">
          <PostImage alt={post.web_title} radiusClass={radiusClass} src={post.image_url} />
        </div>

        <div className={cx('flex h-full w-full flex-col justify-between space-y-6 p-4')}>
          <div>
            <div className="flex justify-between space-x-4">
              <div className="space-y-1">
                {isPremium && <Premium color={card.premium_text_color} fontFamily={card.font} />}
                <Titles
                  title={post.web_title}
                  subtitle={post?.web_subtitle}
                  fontFamily={fontFamily}
                  titleSize="xl"
                  subtitleSize="sm"
                  titleColor={card.text_color}
                  subtitleColor={card.text_color}
                  textClassName={card.hover_underline === 'underline' ? 'group-hover:underline' : ''}
                />
              </div>
            </div>

            <div className="pt-6">
              <Details
                authors={post.authors}
                time={post.override_scheduled_at}
                fontFamily={fontFamily}
                color={card.timestamp_text_color}
              />
            </div>
          </div>

          {card?.tags_enabled && (
            <div className="space-y-2">
              <Tags
                tags={post.content_tags}
                fontFamily={fontFamily}
                bgColor={card.tags_bg_color}
                color={card.tags_text_color}
              />
            </div>
          )}

          <Insights
            post={post}
            likeCount={likeCount}
            commentCount={commentCount}
            commentsEnabled={post.comments_enabled}
            fontFamily={fontFamily}
            color={card.text_color}
          />
        </div>
      </PostLink>
    </div>
  );
};

export default PostCardv2;
